import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  MapPin,
  Plus,
  Pencil,
  Trash2,
  Star,
  Phone,
} from "lucide-react";
import {
  Button,
  Alert,
  AlertDialog,
  EmptyState,
  LoadingState,
} from "@printtat/ui";
import { AddressFormFields } from "@/components/forms/AddressForm";
import { addressApi } from "@/services/addressApi";
import { queryKeys } from "@/lib/queryKeys";
import type { SavedAddress, CreateAddressDto } from "@printtat/shared";

const MAX_ADDRESSES = 10;

const addressSchema = z.object({
  label: z.string().min(1, "Label is required"),
  fullName: z.string().min(1, "Full name is required"),
  phone: z.string().min(1, "Phone number is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  governorate: z.string().min(1, "Governorate is required"),
  postalCode: z.string().optional(),
});

const emptyFormValues = {
  label: "",
  fullName: "",
  phone: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  governorate: "",
  postalCode: "",
};

export const AddressSettings = () => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const {
    data: addresses = [],
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: queryKeys.addresses.all(),
    queryFn: () => addressApi.getAddresses(),
    staleTime: 30_000,
  });

  const displayError =
    error ||
    (fetchError
      ? fetchError instanceof Error
        ? fetchError.message
        : "Failed to load addresses"
      : null);

  const form = useForm({
    defaultValues: emptyFormValues,
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const parsed = addressSchema.parse(value);
        const dto: CreateAddressDto = {
          ...parsed,
          addressLine2: parsed.addressLine2 || undefined,
          postalCode: parsed.postalCode || undefined,
        };

        if (editingId) {
          await addressApi.updateAddress(editingId, dto);
        } else {
          await addressApi.createAddress(dto);
        }

        await queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all() });
        closeForm();
      } catch (err) {
        if (err instanceof z.ZodError) {
          setError(err.issues.map((i) => i.message).join(", "));
        } else {
          setError(err instanceof Error ? err.message : "Failed to save address");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const openAddForm = () => {
    setEditingId(null);
    form.reset();
    setIsFormOpen(true);
    setError(null);
  };

  const openEditForm = (address: SavedAddress) => {
    setEditingId(address.id);
    form.setFieldValue("label", address.label);
    form.setFieldValue("fullName", address.fullName);
    form.setFieldValue("phone", address.phone);
    form.setFieldValue("addressLine1", address.addressLine1);
    form.setFieldValue("addressLine2", address.addressLine2 || "");
    form.setFieldValue("city", address.city);
    form.setFieldValue("governorate", address.governorate);
    form.setFieldValue("postalCode", address.postalCode || "");
    setIsFormOpen(true);
    setError(null);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    form.reset();
    setError(null);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await addressApi.deleteAddress(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all() });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete address");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await addressApi.setDefault(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.addresses.all() });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set default");
    }
  };

  if (isLoading) {
    return <LoadingState message="Loading addresses..." className="py-12" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-700" />
            <h2 className="text-sm font-semibold text-gray-900">
              Shipping Addresses
            </h2>
          </div>
          {!isFormOpen && (
            <Button
              size="sm"
              onClick={openAddForm}
              disabled={addresses.length >= MAX_ADDRESSES}
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Address
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500">
          {addresses.length >= MAX_ADDRESSES
            ? `Maximum of ${MAX_ADDRESSES} addresses reached`
            : "Save addresses to use during checkout"}
        </p>
      </div>

      {displayError && <Alert variant="error">{displayError}</Alert>}

      {/* Add/Edit Form */}
      {isFormOpen && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            {editingId ? "Edit Address" : "Add New Address"}
          </h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-4"
          >
            <div className="max-w-xs">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder='e.g. "Home", "Office"'
                value={form.getFieldValue("label")}
                onChange={(e) => form.setFieldValue("label", e.target.value)}
              />
            </div>

            <AddressFormFields form={form} />

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={closeForm}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" loading={isSubmitting}>
                {editingId ? "Save Changes" : "Add Address"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Address List */}
      {addresses.length === 0 && !isFormOpen ? (
        <EmptyState
          icon={MapPin}
          title="No saved addresses"
          description="Add a shipping address to speed up checkout."
          action={
            <Button size="sm" onClick={openAddForm}>
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Address
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {addresses.map((address) => (
            <div
              key={address.id}
              className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">
                    {address.label}
                  </span>
                  {address.isDefault && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                      <Star className="w-3 h-3 fill-current" />
                      Default
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {address.fullName}
                </p>
                <p className="text-sm text-gray-500">
                  {address.addressLine1}
                  {address.addressLine2 && `, ${address.addressLine2}`}
                </p>
                <p className="text-sm text-gray-500">
                  {address.city}, {address.governorate}
                  {address.postalCode && ` ${address.postalCode}`}
                </p>
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  <Phone className="w-3 h-3" />
                  {address.phone}
                </p>
              </div>

              <div className="flex items-center gap-1 flex-shrink-0">
                {!address.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSetDefault(address.id)}
                    className="text-xs"
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditForm(address)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <AlertDialog
                  title="Delete address?"
                  description={`Are you sure you want to delete "${address.label}"? This cannot be undone.`}
                  onConfirm={() => handleDelete(address.id)}
                  variant="destructive"
                  confirmText="Delete"
                  loading={deletingId === address.id}
                >
                  <Button variant="ghost" size="sm" color="red">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
